// src/encounter/lib/to-referral-handoff.ts
// [referralHandoff] Map the EXISTING referral rows to Antonin's hand-off card (@scripsteam/signal ReferralHandoffView):
//   - the sender (catalogue 41, physician's wrap-up): the encounter's saved referrals (PatientAssessment.referrals, the A&P
//     ReferralRequest rows written by the existing referral dialog), from the signed-in physician to the picked practitioner
//     or "Any <speciality>";
//   - the receiver (catalogue 07, the receiving practitioner's pre-visit): the newest open row of
//     GET /api/Patients/{id}/referrals (use-patient-referrals.ts).
// PURE. Plain text only. Nothing is invented: a value the row does not carry (the hand-over results, the booking) is left out,
// and the card then draws nothing for it.
import { clinicDateD45 } from '@/shared/lib/clinic-time';
import type { ReferralHandoffView, ReferralStage } from '@scripsteam/signal';
import type { AssessmentReferral, ReferralStatus, RxDiagnosisCodesRequest } from '@/types/assessment-api';
import type { PatientReferralRow } from '../services/use-patient-referrals';
import { plainOf } from './to-referral-card';

/** The server's status as a step on the designed track. `cancelled` has no step (the sender list leaves it out). */
const STAGE: Record<Exclude<ReferralStatus, 'cancelled'>, ReferralStage> = {
  draft: 'draft',
  sent: 'sent',
  accepted: 'sent',
  booked: 'booked',
  completed: 'seen',
};

function stageOf(status: ReferralStatus | null | undefined, signed: boolean): ReferralStage {
  if (status && status !== 'cancelled') return STAGE[status];
  // No server status yet (an unsaved or pre-PROD-1395 row): a referral is sent when the note is signed.
  return signed ? 'sent' : 'draft';
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The receiving side's name and role: the picked practitioner, else "Any <speciality>" (the design's "Any dietitian"). */
function targetOf(r: AssessmentReferral): { name: string; role?: string } {
  const speciality = r.targetSpecialityName?.trim() || (r.performerRole?.trim() ? cap(r.performerRole.trim()) : '');
  const picked = r.targetPractitionerId ? r.targetDisplay?.trim() : '';
  if (picked) return { name: picked, ...(speciality ? { role: speciality } : {}) };
  if (speciality) return { name: `Any ${speciality.toLowerCase()}`, role: speciality };
  const display = r.targetDisplay?.trim() || r.display?.trim();
  return { name: display || 'Referral' };
}

function whyOf(r: AssessmentReferral): NonNullable<ReferralHandoffView['why']> {
  const rows: RxDiagnosisCodesRequest[] = [...(r.diagnosis ?? []), ...(r.otherDiagnosis ?? [])].filter((d) => !d.isDeleted);
  const seen = new Set<string>();
  const why: NonNullable<ReferralHandoffView['why']> = [];
  const push = (code: string | null | undefined, display: string | null | undefined) => {
    const text = display?.trim();
    if (!text) return;
    const key = `${code ?? ''}|${text}`.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    why.push({ ...(code?.trim() ? { code: code.trim() } : {}), display: text });
  };
  push(r.reasonCode, r.reasonDisplay);
  for (const d of rows) push(d.dxCode?.code, d.dxCode?.displayName);
  return why;
}

function base(r: AssessmentReferral, from: ReferralHandoffView['from'], stage: ReferralStage): ReferralHandoffView {
  const why = whyOf(r);
  const asks = plainOf(r.notePlainText) ?? plainOf(r.notes);
  return {
    from,
    to: targetOf(r),
    stage,
    ...(why.length ? { why } : {}),
    ...(asks ? { asks } : {}),
  };
}

/** The sender's cards (wrap-up): every live referral of this encounter, in the order the physician added them. */
export function sentReferralHandoffs(
  referrals: readonly AssessmentReferral[] | null | undefined,
  opts: {
    fromName?: string;
    fromRole?: string;
    signed: boolean;
    /** The viewer's practitioner id: the viewer's name is shown only on referrals the viewer authored. */
    viewerId?: string | null;
    /** The assessment's `performer` (who wrote the A&P), used when a referral row carries no performer of its own. */
    assessmentPerformer?: string | null;
  },
): (ReferralHandoffView & { id: string })[] {
  const role = opts.fromRole ? { role: opts.fromRole } : {};
  const mine = { name: opts.fromName?.trim() || 'You', ...role };
  // Codex P2: another physician opening the encounter must not see his own name on a referral someone else sent.
  const other = { name: 'Referring physician', ...role };
  const norm = (s: string | null | undefined) => s?.trim().toLowerCase() || '';
  return (referrals ?? [])
    .filter((r) => !r.isDeleted && r.status !== 'cancelled')
    .map((r, i) => {
      const author = norm(r.performer) || norm(opts.assessmentPerformer);
      const from = !author || author === norm(opts.viewerId) ? mine : other;
      return { id: r.id ?? `referral-${i}`, ...base(r, from, stageOf(r.status, opts.signed)) };
    });
}

/** The receiver's card (pre-visit): the incoming referral, as the list row states it. */
export function receivedReferralHandoff(row: PatientReferralRow): ReferralHandoffView {
  const from = { name: row.referrerName?.trim() || 'Referring practitioner', role: 'Physician' };
  const date = clinicDateD45(row.createdDate);
  return { ...base(row, from, stageOf(row.status, true)), ...(date ? { date } : {}) };
}
